/* eslint-disable max-lines-per-function */
import { AppointmentId } from '@dreryk/egabinet-appointment-published-language-api';
import {
   VisitAlreadySettledFault,
   VisitSettlementUpdatePriceChangeReasonCommand,
} from '@dreryk/egabinet-financial-settlement-frontend-api';
import { PurchaseId } from '@dreryk/egabinet-financial-settlement-published-language-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { dispatchSnackbarNegativeEvent, dispatchSnackbarPositiveEvent } from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { PriceChangeReason } from '../../../shared/priceChangeReason/priceChangeReason.types';
import { useRefetchVisitSettlement } from '../../../shared/useRefetchVisitSettlement';
import { visitSettlementTranslationKeys } from '../../../translations';

import { VisitSettlementPriceChangeReasonFormBean } from './VisitSettlementPriceChangeReasonForm';

type VisitSettlementUpdatePriceChangeReasonActionRenderProps = {
   updatePriceChangeReason: (formData: VisitSettlementPriceChangeReasonFormBean) => Promise<void>;
};

type VisitSettlementUpdatePriceChangeReasonActionProps = {
   children: (renderProps: VisitSettlementUpdatePriceChangeReasonActionRenderProps) => JSX.Element;
   appointmentId: number;
   onVisitAlreadySettledFault?: VoidFunction;
   onPriceChangeReason: (priceChangeReason: PriceChangeReason | null) => void;
};

export const VisitSettlementUpdatePriceChangeReasonAction = (
   props: VisitSettlementUpdatePriceChangeReasonActionProps
): JSX.Element => {
   const refetchVisitSettlement = useRefetchVisitSettlement();

   const { executeAsync: updatePriceChangeReason } = useCqrsCommand<
      void,
      VisitSettlementPriceChangeReasonFormBean,
      VisitAlreadySettledFault
   >(
      (parameters) => {
         const command = new VisitSettlementUpdatePriceChangeReasonCommand();
         command.purchaseId = PurchaseId.forAppointment(AppointmentId.of(props.appointmentId));
         command.priceChangeReason = Choice.withUseDefaultIfNull(parameters.priceChangeReason);
         return command;
      },
      {
         onSuccess: (__, parameters) => {
            dispatchSnackbarPositiveEvent(
               visitSettlementTranslationKeys.extensions.visitForm.priceChangeReason.commands.update.successfully
            );
            props.onPriceChangeReason?.(parameters.priceChangeReason);
         },
         onFault: {
            'egabinet-financial-settlement-frontend-api:VisitAlreadySettledFault': () => {
               refetchVisitSettlement();
               dispatchSnackbarNegativeEvent(
                  visitSettlementTranslationKeys.extensions.visitForm.priceChangeReason.commands.update.errors
                     .visitAlreadySettledFault,
                  {
                     autoHideDelayInMs: false,
                  }
               );
               props.onVisitAlreadySettledFault?.();
            },
         },
         onUnexpectedError: () => {
            dispatchSnackbarNegativeEvent(
               visitSettlementTranslationKeys.extensions.visitForm.priceChangeReason.commands.update.errors
                  .unexpectedError
            );
         },
      }
   );

   return props.children({
      updatePriceChangeReason: async (formData) => {
         await updatePriceChangeReason(formData);
      },
   });
};
